/**
 * Shared formatting and status tones for the portal sections.
 * Money comes from `@wa/ui` `formatMoney` (reuse); dates and tones live here.
 */
import { formatMoney, type BadgeProps } from "@wa/ui";

/** Money - narrows a wide locale string to `formatMoney`'s `Locale` type (et|en). */
export function fmtMoney(cents: number, currency: string, locale: string): string {
  return formatMoney(cents, currency, locale === "et" ? "et" : "en");
}

// Money on a document (always with its cents) lives in `money.ts`, which has no
// kit import and can therefore be tested under plain node. Re-exported here so
// the screens keep one import for every formatter.
export { fmtAmount } from "./money";

export function fmtDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString(locale === "en" ? "en-GB" : "et-EE", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "-";
  }
}

export function fmtDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString(locale === "en" ? "en-GB" : "et-EE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
}

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

/** The tone for an invoice status (Badge variant). */
export function invoiceStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case "paid":
      return "success";
    case "overdue":
      return "destructive";
    case "partially_paid":
      return "warning";
    case "cancelled":
      return "muted";
    default:
      // "sent": an open invoice, in the brand tint. It was the blue `info`
      // tone, whose text reads at about 2.3:1 on a light card - too faint for
      // the word that says an invoice is still waiting.
      return "default";
  }
}

/** The general status tone for projects, tickets, quotes and contracts. */
export function genericStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case "completed":
    case "resolved":
    case "active":
    case "accepted":
      return "success";
    case "cancelled":
    case "terminated":
    case "declined":
    case "expired":
    case "closed":
      return "muted";
    case "on_hold":
    case "waiting":
      return "warning";
    case "urgent":
      return "destructive";
    case "in_progress":
      // The brand tint rather than the blue `info` one - see the note on
      // `invoiceStatusVariant`.
      return "default";
    default:
      return "secondary";
  }
}
